/**
 * YouTube Language Support Constants
 *
 * This module contains language mappings specifically for YouTube features:
 * - Auto-caption languages (73 languages)
 * - Intersection with NLLB-200 for subtitle translation
 *
 * All 73 YouTube auto-caption languages are supported by NLLB-200!
 *
 * References:
 * - YouTube Auto-Captions: https://support.google.com/youtube/answer/6373554
 * - YouTube Captions API: https://developers.google.com/youtube/v3/docs/captions
 * - NLLB-200: https://github.com/facebookresearch/fairseq/tree/nllb
 */

import type { LanguageOption } from '../types/language';

// =============================================================================
// YOUTUBE AUTO-CAPTION LANGUAGES (73 TOTAL)
// =============================================================================

/**
 * All YouTube auto-caption languages that are ALSO supported by NLLB-200
 * This enables automatic subtitle translation for YouTube videos
 * Source: https://support.google.com/youtube/answer/6373554
 */
export const YOUTUBE_NLLB_SUPPORTED_LANGUAGES: Record<string, string> = {
  af: 'Afrikaans', // afr_Latn
  am: 'Amharic', // amh_Ethi
  ar: 'Arabic', // arb_Arab
  az: 'Azerbaijani', // azj_Latn
  be: 'Belarusian', // bel_Cyrl
  bg: 'Bulgarian', // bul_Cyrl
  bn: 'Bengali', // ben_Beng
  cs: 'Czech', // ces_Latn
  da: 'Danish', // dan_Latn
  de: 'German', // deu_Latn
  el: 'Greek', // ell_Grek
  en: 'English', // eng_Latn
  es: 'Spanish', // spa_Latn
  et: 'Estonian', // est_Latn
  eu: 'Basque', // eus_Latn
  fa: 'Persian', // pes_Arab
  fi: 'Finnish', // fin_Latn
  fr: 'French', // fra_Latn
  gl: 'Galician', // glg_Latn
  gu: 'Gujarati', // guj_Gujr
  hi: 'Hindi', // hin_Deva
  hr: 'Croatian', // hrv_Latn
  hu: 'Hungarian', // hun_Latn
  hy: 'Armenian', // hye_Armn
  id: 'Indonesian', // ind_Latn
  is: 'Icelandic', // isl_Latn
  it: 'Italian', // ita_Latn
  iw: 'Hebrew', // heb_Hebr
  ja: 'Japanese', // jpn_Jpan
  jv: 'Javanese', // jav_Latn
  ka: 'Georgian', // kat_Geor
  km: 'Central Khmer', // khm_Khmr
  kn: 'Kannada', // kan_Knda
  ko: 'Korean', // kor_Hang
  lo: 'Lao', // lao_Laoo
  lt: 'Lithuanian', // lit_Latn
  lv: 'Latvian', // lvs_Latn
  mk: 'Macedonian', // mkd_Cyrl
  ml: 'Malayalam', // mal_Mlym
  mn: 'Mongolian', // khk_Cyrl
  mr: 'Marathi', // mar_Deva
  ms: 'Malay', // zsm_Latn
  my: 'Burmese', // mya_Mymr
  ne: 'Nepali', // npi_Deva
  nl: 'Dutch', // nld_Latn
  no: 'Norwegian', // nob_Latn
  or: 'Odia', // ory_Orya
  pa: 'Punjabi', // pan_Guru
  pl: 'Polish', // pol_Latn
  ps: 'Pashto', // pbt_Arab
  pt: 'Portuguese', // por_Latn
  ro: 'Romanian', // ron_Latn
  ru: 'Russian', // rus_Cyrl
  si: 'Sinhala', // sin_Sinh
  sk: 'Slovak', // slk_Latn
  sl: 'Slovenian', // slv_Latn
  sq: 'Albanian', // als_Latn
  sr: 'Serbian', // srp_Cyrl
  su: 'Sundanese', // sun_Latn
  sv: 'Swedish', // swe_Latn
  sw: 'Swahili', // swh_Latn
  ta: 'Tamil', // tam_Taml
  te: 'Telugu', // tel_Telu
  th: 'Thai', // tha_Thai
  tl: 'Filipino', // tgl_Latn
  tr: 'Turkish', // tur_Latn
  uk: 'Ukrainian', // ukr_Cyrl
  ur: 'Urdu', // urd_Arab
  uz: 'Uzbek', // uzn_Latn
  vi: 'Vietnamese', // vie_Latn
  yue: 'Cantonese', // zho_Hans
  zh: 'Chinese', // zho_Hans
  zu: 'Zulu', // zul_Latn
};

// =============================================================================
// TOP 30 MOST SPOKEN LANGUAGES (YouTube + NLLB)
// =============================================================================

/**
 * Top 30 most spoken languages worldwide that are supported by both
 * YouTube auto-captions and NLLB-200 translation
 * Ordered by total number of speakers (native + second language)
 */
export const TOP_30_YOUTUBE_LANGUAGES: readonly string[] = [
  'en', // English - 1.5B total
  'zh', // Chinese (Mandarin) - 1.1B total
  'hi', // Hindi - 600M total
  'es', // Spanish - 560M total
  'ar', // Arabic - 420M total
  'bn', // Bengali - 270M total
  'pt', // Portuguese - 260M total
  'ru', // Russian - 260M total
  'ja', // Japanese - 125M total
  'de', // German - 135M total
  'fr', // French - 280M total
  'tr', // Turkish - 90M total
  'ko', // Korean - 82M total
  'vi', // Vietnamese - 85M total
  'it', // Italian - 85M total
  'id', // Indonesian - 200M total
  'ur', // Urdu - 230M total
  'fa', // Persian - 110M total
  'pl', // Polish - 45M total
  'uk', // Ukrainian - 40M total
  'ta', // Tamil - 80M total
  'te', // Telugu - 95M total
  'ms', // Malay - 290M total
  'sw', // Swahili - 200M total
  'gu', // Gujarati - 60M total
  'kn', // Kannada - 45M total
  'ml', // Malayalam - 38M total
  'mr', // Marathi - 83M total
  'th', // Thai - 60M total
  'nl', // Dutch - 25M total
] as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the language name for a YouTube language code.
 *
 * @param code - ISO 639-1 language code (e.g., 'en', 'pt', 'zh')
 * @returns Language name (e.g., 'English', 'Portuguese', 'Chinese') or empty string if not found
 *
 * @example
 * getYouTubeLanguageName('en') // 'English'
 * getYouTubeLanguageName('pt') // 'Portuguese'
 */
export function getYouTubeLanguageName(code: string): string {
  return YOUTUBE_NLLB_SUPPORTED_LANGUAGES[code] || '';
}

/**
 * Get all YouTube auto-caption language codes.
 *
 * @returns Array of all language codes (sorted alphabetically)
 *
 * @example
 * const codes = getAllYouTubeCodes();
 * codes.length // 73
 */
export function getAllYouTubeCodes(): string[] {
  return Object.keys(YOUTUBE_NLLB_SUPPORTED_LANGUAGES).sort();
}

/**
 * Top-20 YouTube languages by global speaker count, in the BCP-47 codes used by
 * the YouTube publish catalog (GET /api/v1/youtube/languages).
 *
 * Mirror of the backend source of truth
 * `constants/youtube_publish/youtube_api.py::YOUTUBE_TOP_20_LANGUAGE_CODES`.
 * Kept here (a constants module, not a component) so the YouTube target-language
 * selector's "Top 20" quick action matches the AI Director selector UX without
 * hardcoding the list inside the component (RULE #1).
 */
export const YOUTUBE_TOP_20_LANGUAGE_CODES: string[] = [
  'en',
  'zh-CN',
  'hi',
  'es',
  'fr',
  'ar',
  'bn',
  'pt',
  'ru',
  'ja',
  'de',
  'ko',
  'vi',
  'te',
  'mr',
  'ta',
  'tr',
  'it',
  'ur',
  'th',
];

/**
 * Get YouTube languages formatted for UI dropdowns/selectors.
 *
 * @returns Array of language options with code and name
 *
 * @example
 * const langs = getYouTubeLanguagesForUI();
 * langs[0] // { code: 'af', name: 'Afrikaans' }
 */
export function getYouTubeLanguagesForUI(): LanguageOption[] {
  return Object.entries(YOUTUBE_NLLB_SUPPORTED_LANGUAGES)
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get top 30 languages formatted for UI dropdowns/selectors.
 *
 * @returns Array of language options with code and name (in popularity order)
 *
 * @example
 * const langs = getTop30ForUI();
 * langs.length // 30
 * langs[0] // { code: 'en', name: 'English' }
 */
export function getTop30ForUI(): LanguageOption[] {
  return TOP_30_YOUTUBE_LANGUAGES.map((code) => ({
    code,
    name: YOUTUBE_NLLB_SUPPORTED_LANGUAGES[code],
  }));
}

// =============================================================================
// STATISTICS
// =============================================================================

export const TOTAL_YOUTUBE_LANGUAGES = Object.keys(YOUTUBE_NLLB_SUPPORTED_LANGUAGES).length; // 73
export const TOTAL_TOP_30 = TOP_30_YOUTUBE_LANGUAGES.length; // 30
